/**
 * DOM-Snapshot der Design-Fläche (Warenkorb-Thumbnail, Vorlagen-Vorschau).
 * Nutzt html-to-image; Bilder müssen CORS-fähig sein (S3-Previews sind es, data:-URLs sowieso).
 */
import { toPng, toJpeg } from 'html-to-image'
import { loadedFontSources } from './fonts/fontLoader'

export type SnapshotOptions = {
  /** Zielbreite in px (Standard 600) */
  widthPx?: number
  format?: 'png' | 'jpeg'
  quality?: number
  backgroundColor?: string
}

/** Rendert `.dr-root` (oder das übergebene Element) als Data-URL. */
export async function snapshotSurface(root: HTMLElement, opts: SnapshotOptions = {}): Promise<string> {
  const rect = root.getBoundingClientRect()
  const targetW = Math.max(64, opts.widthPx ?? 600)
  const pixelRatio = Math.max(0.1, Math.min(4, targetW / Math.max(1, rect.width)))
  if (typeof (document as any).fonts?.ready?.then === 'function') {
    try { await (document as any).fonts.ready } catch { /* egal */ }
  }
  // Nur die Design-Schriften einbetten. Ohne fontEmbedCSS liest html-to-image sämtliche Stylesheets
  // der Seite (im Admin > 70 Schriften) – das dauert dort ~30 s und scheitert an fremden CSS-Dateien.
  const fontEmbedCSS = await designFontEmbedCss()
  const common = { pixelRatio, cacheBust: false, backgroundColor: opts.backgroundColor ?? '#ffffff', fontEmbedCSS }
  return opts.format === 'jpeg'
    ? toJpeg(root, { ...common, quality: opts.quality ?? 0.9 })
    : toPng(root, common)
}

const fontDataUrlCache = new Map<string, Promise<string>>()
function fontAsDataUrl(url: string): Promise<string> {
  let job = fontDataUrlCache.get(url)
  if (!job) {
    job = fetch(url, { mode: 'cors' })
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.blob() })
      .then((blob) => new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => reject(reader.error)
        reader.readAsDataURL(blob)
      }))
    job.catch(() => fontDataUrlCache.delete(url))
    fontDataUrlCache.set(url, job)
  }
  return job
}

async function designFontEmbedCss(): Promise<string> {
  const rules = await Promise.all(loadedFontSources().map(async (f) => {
    try {
      const data = await fontAsDataUrl(f.url)
      return `@font-face{font-family:"${f.family.replace(/"/g, '')}";font-weight:${f.weight};font-style:${f.italic ? 'italic' : 'normal'};src:url(${data}) format("woff2");}`
    } catch {
      return ''
    }
  }))
  return rules.filter(Boolean).join('\n')
}

export function dataUrlToFile(dataUrl: string, filename: string): File {
  const [head, body] = dataUrl.split(',')
  const mime = /data:([^;]+)/.exec(head || '')?.[1] || 'image/png'
  const bin = atob(body || '')
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new File([bytes], filename, { type: mime })
}
